import * as React from 'react';
import { Clock } from 'lucide-react';

import { useSession } from '@/app/providers/session-provider';

/**
 * Wall clock in the business timezone.
 *
 * Driven off the bootstrap `server_time` offset rather than the browser clock, so
 * a machine with a wrong system time still shows the time the backend is
 * reasoning about — the timestamps in every list come from the same source.
 */
export function ServerClock() {
  const { bootstrap, serverSkewMs } = useSession();
  const [now, setNow] = React.useState(() => new Date(Date.now() + serverSkewMs));

  React.useEffect(() => {
    // Tick on the second boundary so the display does not sit a fraction behind.
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      const current = Date.now() + serverSkewMs;
      setNow(new Date(current));
      timer = setTimeout(schedule, 1000 - (current % 1000));
    };
    schedule();
    return () => clearTimeout(timer);
  }, [serverSkewMs]);

  const formatter = React.useMemo(() => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        timeZone: bootstrap.timezone || undefined,
        timeZoneName: 'short',
      });
    } catch {
      // An unknown IANA name would throw; fall back to the browser zone.
      return new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
      });
    }
  }, [bootstrap.timezone]);

  return (
    <span
      className="hidden items-center gap-1.5 text-[11px] tabular-nums text-ink-3 sm:flex"
      title={`Server time (${bootstrap.timezone})`}
    >
      <Clock className="size-3.5 text-ink-4" />
      {formatter.format(now)}
    </span>
  );
}
